import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import {
    Box,
    Button,
    Container,
    Grid,
    Paper,
    Typography,
    useMediaQuery,
    Link as MuiLink,
    Divider,
    Alert,
} from '@mui/material';
import moment from 'moment';
import React, { useMemo, useState } from 'react';
import { Link as RouterLink, useLocation, useNavigate } from 'react-router-dom';
import { CustomButton } from '../../../../layout/styledcomponents/CustomButton';
import LoadingComponent from '../../../../layout/styledcomponents/LoadingAnimations/LoadingComponent';
import UserRole from '../../../../shared/AuthRoles';
import { camelCaseToTitleCase, capitalizeName } from '../../../../shared/utils';
import CancelReservationModal from './CancelReservationModal';
import { theme } from '../../../../shared/theme';
import RecurringReservationSummaryList from './RecurringReservationSummaryList';

const safeFormatDate = (date: any, fmt: string = 'DD/MM/YYYY') => {
    if (!date) return '';
    const m = moment(date);
    if (!m.isValid()) return '';
    return m.format(fmt);
};

export default function ViewRecurringReservation(params: any) {
    const [open, setOpen] = useState(false);
    const loggedInUser = UserRole();
    const isOwner = loggedInUser?.role?.includes('owner');
    const navigate = useNavigate();
    const location = useLocation();
    const isMobile = useMediaQuery('(max-width: 770px)');
    const isIpad = useMediaQuery('(max-width: 1200px)');

    const state: any = location.state || {};

    const getReservationDetails =
        params?.reservationDetails ?? state?.reservationDetails ?? null;

    const getIndividualAsset =
        params?.individualAsset ?? state?.individualAsset ?? null;

    const reservations = useMemo(() => {
        if (!Array.isArray(getReservationDetails)) return [];
        return getReservationDetails;
    }, [getReservationDetails]);

    const first = reservations?.[0];

    const recurringId = first?.recurringId ?? null;
    const recurringStatus = first?.status ?? null;
    const createdAt = first?.createdAt ?? null;

    const createdAtDate = useMemo(() => safeFormatDate(createdAt), [createdAt]);

    const shortAddress = useMemo(() => {
        const suburb = getIndividualAsset?.suburb ?? '';
        const postcode = getIndividualAsset?.postcode ?? '';
        const stateVal = getIndividualAsset?.state ?? '';
        const countryCode = getIndividualAsset?.countryCode ?? '';
        return `${suburb} ${postcode} ${stateVal} ${countryCode}`.trim();
    }, [
        getIndividualAsset?.suburb,
        getIndividualAsset?.postcode,
        getIndividualAsset?.state,
        getIndividualAsset?.countryCode,
    ]);

    const assetImageSrc = getIndividualAsset?.photos?.[0]?.src;

    const isModuleAvailable =
        getIndividualAsset?.module?.isAvailable !== false &&
        getIndividualAsset?.isAvailable !== false;

    const recurringReason =
        first?.reason && String(first?.reason).trim().length > 0
            ? first.reason
            : null;

    const bookedBy = first?.booker
        ? `${first.booker?.firstName ?? ''} ${first.booker?.lastName ?? ''}`.trim()
        : '';

    const labelSx = {
        fontWeight: 800,
        color: theme.palette.text.secondary,
        lineHeight: 1.35,
    };

    const valueSx = {
        color: theme.palette.text.primary,
        lineHeight: 1.35,
        wordBreak: 'break-word' as const,
    };

    const infoCardSx = {
        mb: 1.25,
        borderRadius: 2,
        border: `1px solid ${theme.palette.custom.lightBorderColor}`,
        backgroundColor: theme.palette.neutral.main,
        px: 1.5,
        py: 1.1,
        display: 'flex',
        alignItems: 'center',
        gap: 1.25,
        position: 'relative' as const,
        overflow: 'hidden',
        '&:hover': {
            backgroundColor: theme.palette.custom.cardActionBackground,
        },
    };

    const onClickGoBack = () => navigate(-1);
    const onClickOpenModal = () => setOpen(true);
    const handleCloseModal = () => setOpen(false);

    const LinkSx = {
        color: 'inherit',
        textDecoration: 'underline',
        textUnderlineOffset: '3px',
        transition: 'color 0.2s ease',
        '&:hover': { color: 'secondary.main' },
    };

    const ExperienceLink = ({
        type,
        id,
        name,
    }: {
        type: string;
        id: string | number;
        name: string;
    }) => (
        <MuiLink
            component={RouterLink}
            to={`/viewExperiences/${type}/${id}`}
            sx={LinkSx}
        >
            {name}
        </MuiLink>
    );

    const ValueLink = ({
        to,
        children,
    }: {
        to: string;
        children: React.ReactNode;
    }) => (
        <Box
            component='a'
            href={to}
            target='_blank'
            rel='noreferrer'
            sx={LinkSx}
        >
            {children}
        </Box>
    );

    const isMissingData = !getReservationDetails || !getIndividualAsset;

    if (isMissingData) {
        return (
            <Container
                maxWidth={isOwner ? 'xl' : 'lg'}
                sx={{ px: { xs: 0.5, sm: 2, md: 0 }, pb: 5 }}
            >
                <Paper
                    elevation={0}
                    square
                    sx={{
                        backgroundColor: theme.palette.neutral.main,
                        p: { xs: 2, sm: 3 },
                        border: `1px solid ${theme.palette.custom.lightBorderColor}`,
                        boxShadow: 'none',
                        borderRadius: 2,
                        mt: 2,
                    }}
                >
                    <Alert severity='warning' sx={{ borderRadius: 2 }}>
                        This recurring reservation can’t be loaded because the
                        page didn’t receive its data. Go back and open it again
                        from the reservations list.
                    </Alert>

                    <Box
                        sx={{
                            display: 'flex',
                            gap: 1,
                            mt: 2,
                            flexWrap: 'wrap',
                        }}
                    >
                        <Button
                            variant='outlined'
                            onClick={onClickGoBack}
                            sx={{
                                borderRadius: 2,
                                textTransform: 'none' as const,
                            }}
                        >
                            Go Back
                        </Button>

                        <Button
                            variant='contained'
                            onClick={() => navigate('/reservations')}
                            sx={{
                                borderRadius: 2,
                                textTransform: 'none' as const,
                            }}
                        >
                            View Reservations
                        </Button>
                    </Box>
                </Paper>

                <Grid
                    sx={{
                        height: isIpad ? '30vh' : isMobile ? '5px' : '30vh',
                        width: '100%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        opacity: 0.4,
                    }}
                >
                    <LoadingComponent />
                </Grid>
            </Container>
        );
    }

    return (
        <Container
            maxWidth={isOwner ? 'xl' : 'lg'}
            sx={{ px: { xs: 0.5, sm: 2, md: 0 }, pb: 5 }}
        >
            <Box
                sx={{
                    display: 'flex',
                    alignItems: { xs: 'flex-start', sm: 'center' },
                    justifyContent: 'space-between',
                    gap: 2,
                    flexWrap: 'wrap',
                    mb: 2,
                }}
            >
                <Box sx={{ minWidth: 0 }}>
                    <Typography
                        variant='h1'
                        sx={{
                            textAlign: 'left',
                            textTransform: 'uppercase',
                            fontSize: { xs: '20px', sm: '22px', md: '24px' },
                            lineHeight: 1.2,
                            m: 0,
                            pr: 1,
                        }}
                    >
                        {getIndividualAsset?.name}
                    </Typography>

                    <Box
                        sx={{
                            display: 'flex',
                            gap: 1,
                            flexWrap: 'wrap',
                            mt: 0.75,
                        }}
                    >
                        <Typography
                            sx={{
                                fontSize: 12,
                                color: theme.palette.text.secondary,
                            }}
                        >
                            Recurring Reservation #{recurringId ?? '-'}
                        </Typography>

                        <Typography
                            sx={{
                                fontSize: 12,
                                color: theme.palette.text.secondary,
                            }}
                        >
                            •
                        </Typography>

                        <Typography
                            sx={{
                                fontSize: 12,
                                color: theme.palette.text.secondary,
                            }}
                        >
                            {createdAtDate ? `Created ${createdAtDate}` : ''}
                        </Typography>
                    </Box>
                </Box>
            </Box>

            <Grid container spacing={2} alignItems='flex-start'>
                <Grid
                    item
                    xs={12}
                    md={8}
                    lg={8.5}
                    sx={{ order: { xs: 1, md: 0 } }}
                >
                    {assetImageSrc && isModuleAvailable ? (
                        <Box
                            sx={{
                                mb: 2,
                                borderRadius: 2,
                                overflow: 'hidden',
                                border: `1px solid ${theme.palette.custom.lightBorderColor}`,
                                backgroundColor: theme.palette.neutral.main,
                            }}
                        >
                            <Box
                                sx={{
                                    position: 'relative',
                                    height: { xs: 160, sm: 200, md: 240 },
                                    width: '100%',
                                }}
                            >
                                <Box
                                    component='img'
                                    src={assetImageSrc}
                                    alt={getIndividualAsset?.name}
                                    sx={{
                                        width: '100%',
                                        height: '100%',
                                        objectFit: 'cover',
                                        display: 'block',
                                    }}
                                />
                                <Box
                                    sx={{
                                        position: 'absolute',
                                        inset: 0,
                                        background:
                                            'linear-gradient(to top, rgba(0,0,0,0.45), rgba(0,0,0,0) 55%)',
                                    }}
                                />
                                <Box
                                    sx={{
                                        position: 'absolute',
                                        left: 16,
                                        right: 16,
                                        bottom: 12,
                                        display: 'flex',
                                        alignItems: 'flex-end',
                                        justifyContent: 'space-between',
                                        gap: 1.5,
                                    }}
                                >
                                    <Box sx={{ minWidth: 0 }}>
                                        <Typography
                                            sx={{
                                                color: theme.palette.neutral
                                                    .main,
                                                fontWeight: 900,
                                                fontSize: 14,
                                                lineHeight: 1.2,
                                                textShadow:
                                                    '0 1px 2px rgba(0,0,0,0.35)',
                                                whiteSpace: 'nowrap',
                                                overflow: 'hidden',
                                                textOverflow: 'ellipsis',
                                            }}
                                        >
                                            {camelCaseToTitleCase(
                                                getIndividualAsset?.type,
                                            )}
                                        </Typography>
                                        {getIndividualAsset?.suburb ? (
                                            <Typography
                                                sx={{
                                                    mt: 0.25,
                                                    color: 'rgba(255,255,255,0.9)',
                                                    fontWeight: 700,
                                                    fontSize: 12,
                                                    lineHeight: 1.2,
                                                    textShadow:
                                                        '0 1px 2px rgba(0,0,0,0.3)',
                                                    whiteSpace: 'nowrap',
                                                    overflow: 'hidden',
                                                    textOverflow: 'ellipsis',
                                                }}
                                            >
                                                {getIndividualAsset?.suburb}
                                                {getIndividualAsset?.state
                                                    ? `, ${getIndividualAsset?.state}`
                                                    : ''}
                                            </Typography>
                                        ) : null}
                                    </Box>
                                </Box>
                            </Box>
                        </Box>
                    ) : null}

                    <Paper
                        elevation={0}
                        square
                        sx={{
                            backgroundColor: theme.palette.neutral.main,
                            p: { xs: 2, sm: 3, md: 3.5 },
                            border: `1px solid ${theme.palette.custom.lightBorderColor}`,
                            boxShadow: 'none',
                            borderRadius: 2,
                            mb: 2,
                        }}
                    >
                        <Box
                            sx={{
                                display: 'flex',
                                flexDirection: 'column',
                                gap: 2,
                            }}
                        >
                            <Typography
                                component='h2'
                                variant='body1'
                                sx={{ fontWeight: 700, letterSpacing: 0.2 }}
                            >
                                Recurring Reservations
                            </Typography>
                            <Divider />
                            <RecurringReservationSummaryList
                                recurringId={recurringId ?? ''}
                                createdAt={createdAt}
                                reservations={reservations}
                                onOpenReservation={(id) =>
                                    navigate(`/reservations/experience/${id}`)
                                }
                            />
                        </Box>
                    </Paper>

                    <Box sx={{ display: isMobile ? 'block' : 'none' }}>
                        <CustomButton
                            onClick={onClickGoBack}
                            color='primary'
                            sx={{
                                marginLeft: '-8px',
                                '&:hover': {
                                    cursor: 'pointer',
                                    backgroundColor: 'transparent',
                                    background: 'none',
                                },
                            }}
                        >
                            <ArrowBackIcon />
                            <Typography
                                sx={{ paddingLeft: '5px', fontSize: '12px' }}
                            >
                                Back
                            </Typography>
                        </CustomButton>
                    </Box>
                </Grid>

                <Grid
                    item
                    xs={12}
                    md={4}
                    lg={3.5}
                    sx={{ order: { xs: 0, md: 1 } }}
                >
                    <Paper
                        elevation={0}
                        square
                        sx={{
                            backgroundColor: theme.palette.neutral.main,
                            border: `1px solid ${theme.palette.custom.lightBorderColor}`,
                            borderLeft: `4px solid ${theme.palette.info.main}`,
                            boxShadow: 'none',
                            borderRadius: 2,
                            overflow: 'hidden',
                            position: { xs: 'static', md: 'sticky' },
                            top: { md: 12 },
                            zIndex: 4,
                        }}
                    >
                        <Box
                            sx={{
                                px: 2.25,
                                pt: 2,
                                pb: 1.75,
                                backgroundColor:
                                    theme.palette.custom.cardActionBackground,
                                borderBottom: `1px solid ${theme.palette.custom.lightBorderColor}`,
                            }}
                        >
                            <Typography
                                component='h2'
                                variant='body1'
                                sx={{
                                    fontWeight: 900,
                                    letterSpacing: 0.2,
                                    fontSize: 15,
                                    lineHeight: 1.15,
                                    mb: 1.25,
                                }}
                            >
                                Summary
                            </Typography>

                            <Box sx={{ ...infoCardSx, cursor: 'default' }}>
                                <Box sx={{ minWidth: 0, flex: 1 }}>
                                    <Typography
                                        sx={{
                                            fontSize: 12,
                                            fontWeight: 900,
                                            lineHeight: 1.2,
                                            whiteSpace: 'nowrap',
                                            overflow: 'hidden',
                                            textOverflow: 'ellipsis',
                                        }}
                                    >
                                        Recurring reservation · #
                                        {recurringId ?? '—'}
                                    </Typography>
                                    <Typography
                                        sx={{
                                            mt: 0.2,
                                            fontSize: 12,
                                            fontWeight: 650,
                                            color: theme.palette.text.secondary,
                                            lineHeight: 1.2,
                                            whiteSpace: 'nowrap',
                                            overflow: 'hidden',
                                            textOverflow: 'ellipsis',
                                        }}
                                    >
                                        {createdAtDate
                                            ? `Created ${createdAtDate}`
                                            : 'Created —'}
                                    </Typography>
                                </Box>
                            </Box>

                            <Box sx={infoCardSx}>
                                <Box sx={{ minWidth: 0, flex: 1 }}>
                                    <Typography variant='body3' sx={labelSx}>
                                        Experience
                                    </Typography>

                                    <Typography variant='body3' sx={valueSx}>
                                        <ExperienceLink
                                            type={getIndividualAsset?.type}
                                            id={getIndividualAsset?.id}
                                            name={getIndividualAsset?.name}
                                        />{' '}
                                        – hosted by{' '}
                                        {getIndividualAsset?.owner?.name}
                                    </Typography>

                                    <Typography
                                        variant='body3'
                                        sx={{ ...labelSx, mt: 1.25 }}
                                    >
                                        Type
                                    </Typography>
                                    <Typography variant='body3' sx={valueSx}>
                                        {getIndividualAsset?.type
                                            ? capitalizeName(
                                                  getIndividualAsset?.type,
                                              )
                                            : '—'}
                                    </Typography>
                                </Box>
                            </Box>

                            <Box sx={infoCardSx}>
                                <Box sx={{ minWidth: 0, flex: 1 }}>
                                    <Typography variant='body3' sx={labelSx}>
                                        Location
                                    </Typography>
                                    <Typography variant='body3' sx={valueSx}>
                                        <ValueLink
                                            to={`https://www.google.com/maps/place/${encodeURIComponent(shortAddress)}`}
                                        >
                                            {shortAddress}
                                        </ValueLink>
                                    </Typography>
                                </Box>
                            </Box>

                            {recurringReason ? (
                                <Box sx={infoCardSx}>
                                    <Box sx={{ minWidth: 0, flex: 1 }}>
                                        <Typography
                                            variant='body3'
                                            sx={labelSx}
                                        >
                                            Booked By
                                        </Typography>
                                        <Typography
                                            variant='body3'
                                            sx={valueSx}
                                        >
                                            {bookedBy
                                                ? `${bookedBy} via 7DAY AI Chat Assistant`
                                                : '—'}
                                        </Typography>

                                        <Typography
                                            variant='body3'
                                            sx={{ ...labelSx, mt: 1.25 }}
                                        >
                                            Reason
                                        </Typography>
                                        <Typography
                                            variant='body3'
                                            sx={valueSx}
                                        >
                                            {capitalizeName(recurringReason)}
                                        </Typography>
                                    </Box>
                                </Box>
                            ) : null}

                            {first?.notes &&
                            String(first.notes).trim().length > 0 ? (
                                <Box sx={infoCardSx}>
                                    <Box sx={{ minWidth: 0, flex: 1 }}>
                                        <Typography
                                            variant='body3'
                                            sx={labelSx}
                                        >
                                            Notes
                                        </Typography>
                                        <Typography
                                            variant='body3'
                                            sx={valueSx}
                                        >
                                            {first.notes}
                                        </Typography>
                                    </Box>
                                </Box>
                            ) : null}

                            {recurringStatus === 'cancelled' ? (
                                <Box />
                            ) : (
                                <Button
                                    fullWidth
                                    variant='outlined'
                                    color='primary'
                                    sx={{
                                        borderRadius: 2,
                                        height: 36,
                                        textTransform: 'none' as const,
                                        whiteSpace: 'nowrap' as const,
                                    }}
                                    disabled={loggedInUser?.viewOnly}
                                    onClick={onClickOpenModal}
                                >
                                    Cancel Recurring Reservation
                                </Button>
                            )}
                        </Box>
                    </Paper>

                    <Box sx={{ display: isMobile ? 'none' : 'block', mt: 1.5 }}>
                        <Button
                            fullWidth
                            variant='outlined'
                            color='primary'
                            sx={{
                                borderRadius: 2,
                                height: 36,
                                textTransform: 'none' as const,
                                whiteSpace: 'nowrap' as const,
                            }}
                            disabled={loggedInUser?.viewOnly}
                            onClick={onClickGoBack}
                        >
                            Go Back
                        </Button>
                    </Box>
                </Grid>
            </Grid>

            <CancelReservationModal
                open={open}
                onClose={handleCloseModal}
                onChange={handleCloseModal}
                message='To cancel this reservation, please select a reason.'
                component={'recurringReservation'}
                id={recurringId}
            />
        </Container>
    );
}
